import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { EventWebsiteMetricWidget } from 'src/components/Listings/InventoryEventPage/DashboardSection/Widgets/EventWebsiteMetric/EventWebsiteMetricWidget';
import { useOnSaleEventsContext } from 'src/contexts/OnSaleEventsContext/OnSaleEventsContext';
import { Stack } from 'src/core/ui';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionInfo } from 'src/WebApiController';

import * as styles from './OnSaleEventMetrics.css';

export const OnSaleEventMetricsTiles = ({
  selectedSections,
}: {
  selectedSections?: SectionInfo[];
}) => {
  const { sectionMetrics } = useOnSaleEventsContext();

  const selectedSectionIds = useMemo(
    () => selectedSections?.map(({ id }) => id) ?? [],
    [selectedSections]
  );

  const filteredSectionMetrics = useMemo(() => {
    if (isEmpty(selectedSectionIds)) {
      return sectionMetrics;
    }
    return sectionMetrics.filter(({ sectionId }) =>
      selectedSectionIds.includes(sectionId)
    );
  }, [selectedSectionIds, sectionMetrics]);

  const [availableTickets, pageVisits, transactions] = useMemo(() => {
    let currentVisits = 0,
      previousVisits = 0,
      currentTransactions = 0,
      previousTransactions = 0,
      currentTickets = 0,
      previousTickets = 0;
    for (const {
      pageVisits,
      transactions,
      availableTickets,
    } of filteredSectionMetrics) {
      currentVisits += pageVisits?.currentMetricsValue ?? 0;
      previousVisits += pageVisits?.previousMetricsValue ?? 0;
      currentTransactions += transactions?.currentMetricsValue ?? 0;
      previousTransactions += transactions?.previousMetricsValue ?? 0;
      currentTickets += availableTickets?.currentMetricsValue ?? 0;
      previousTickets += availableTickets?.previousMetricsValue ?? 0;
    }
    return [
      {
        currentValue: currentTickets,
        previousValue: previousTickets,
      },
      {
        currentValue: currentVisits,
        previousValue: previousVisits,
      },
      {
        currentValue: currentTransactions,
        previousValue: previousTransactions,
      },
    ];
  }, [filteredSectionMetrics]);

  const convRate = useMemo(() => {
    return {
      currentValue:
        pageVisits.currentValue > 0
          ? (transactions?.currentValue ?? 0) / pageVisits.currentValue
          : 0,
      previousValue:
        pageVisits.previousValue > 0
          ? (transactions?.previousValue ?? 0) / pageVisits.previousValue
          : 0,
    };
  }, [
    pageVisits.currentValue,
    pageVisits.previousValue,
    transactions?.currentValue,
    transactions?.previousValue,
  ]);

  return (
    <Stack direction="responsive" gap="m" width="full">
      <EventWebsiteMetricWidget
        titleContentId={ContentId.TicketsOnPlatform}
        metric={availableTickets}
        rootClassName={styles.OnSaleEventMetricsTile}
        metricsValueClassName={styles.onSaleEventMetricsValue}
        isCompact
      />
      <EventWebsiteMetricWidget
        titleContentId={ContentId.UniqueVisitors}
        metric={pageVisits}
        rootClassName={styles.OnSaleEventMetricsTile}
        metricsValueClassName={styles.onSaleEventMetricsValue}
        isCompact
      />
      <EventWebsiteMetricWidget
        titleContentId={ContentId.Transactions}
        metric={transactions}
        rootClassName={styles.OnSaleEventMetricsTile}
        metricsValueClassName={styles.onSaleEventMetricsValue}
        isCompact
      />
      <EventWebsiteMetricWidget
        titleContentId={ContentId.BuyerConversionRate}
        metric={convRate}
        rootClassName={styles.OnSaleEventMetricsTile}
        metricsValueClassName={styles.onSaleEventMetricsValue}
        isCompact
        isPercentage
      />
    </Stack>
  );
};
